import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { hueFor, initials } from './avatar';
import { decodeHtmlEntities } from '../../lib/decode-entities';
import { EmailDraftCard } from './EmailDraftCard';
import type { InboxCardData, EmailDraftCardData, ResultCardKind } from './types';

type InboxChip = InboxCardData['emails'][number];

type CardAction = (
  a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose'; data: any },
) => void | Promise<boolean | { subject?: string; body?: string } | null | void>;

type ReplyDraftFields = { to: string; subject: string; body: string; cc: string; bcc: string };

/**
 * Session-scoped stash of in-progress reply edits, keyed by messageId
 * (escape-layer contract: closing a dirty layer must never silently discard
 * the user's work). Every keystroke in the reply composer lands here via
 * EmailDraftCard's `onEdit`, so Esc / backdrop / "← Inbox" can dismiss the
 * overlay losslessly — reopening the same chip re-seeds the composer from the
 * stash. Cleared on successful send. Module-level (like EmailDraftCard's
 * sentRegistry) so the draft also survives card re-mounts within the session;
 * mirrors ComposeEmailModal's dismiss-time draft-stash pattern.
 */
const replyDraftStash = new Map<string, ReplyDraftFields>();

/** Test-only: reset the stash so specs stay order-independent. */
export function __resetReplyDraftStashForTests(): void {
  replyDraftStash.clear();
}

/**
 * The priority inbox as a clickable grid of email "chips" — the emails the user
 * needs to deal with. Clicking a chip expands an inline reply composer
 * (EmailDraftCard in reply mode) with a "Xavior write" option that drafts a
 * contextual reply; send delivers in-thread.
 *
 * Mirrors ContactsGridCard's grid pattern. Reply compose/send route back through
 * `onCardAction` (kind: 'email_draft') so ChatBox owns the network calls.
 */
export function InboxResultCard({
  data,
  onCardAction,
}: {
  data: InboxCardData;
  onCardAction: CardAction;
}) {
  const emails = data.emails ?? [];
  const [selected, setSelected] = useState<InboxChip | null>(null);
  const replyPanelRef = useRef<HTMLDivElement | null>(null);
  // Escape-layer contract (apps/web/AGENTS.md): the reply overlay wraps an
  // editable composer, so Escape while a field inside it has focus must exit
  // the field first. First Esc blurs; a second, deliberate Esc closes the
  // overlay — losslessly, because every user keystroke is stashed in
  // replyDraftStash (see above), so no close path can discard unsaved edits.
  const handleEscape = () => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      replyPanelRef.current?.contains(active) &&
      (active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active.isContentEditable)
    ) {
      active.blur();
      return;
    }
    setSelected(null);
  };
  // Window-level fallback for when focus sits OUTSIDE the overlay (e.g. on
  // <body> after the clicked chip unmounted). The hook skips events an earlier
  // handler already claimed via preventDefault, so one keypress = one layer.
  useEscapeKey(handleEscape, selected !== null);

  if (!emails.length) return null;

  if (selected) {
    const re = /^re:/i.test(selected.subject) ? selected.subject : `Re: ${selected.subject || '(no subject)'}`;
    // Re-seed from the stash when the user previously dismissed this reply
    // mid-edit, so their in-progress draft comes back instead of the pristine
    // aiDraft. A stashed non-empty body also suppresses EmailDraftCard's
    // on-mount auto-draft (it only fires on an empty body), so the recovered
    // text is never clobbered.
    const stashed = replyDraftStash.get(selected.messageId);
    const replyData: EmailDraftCardData = {
      to: stashed?.to ?? selected.fromEmail,
      toName: selected.fromName || selected.fromEmail,
      subject: stashed?.subject ?? re,
      body: stashed?.body ?? selected.aiDraft ?? '',
      ...(stashed?.cc ? { cc: stashed.cc } : {}),
      ...(stashed?.bcc ? { bcc: stashed.bcc } : {}),
      threadId: selected.threadId,
      messageId: selected.messageId,
      accountId: selected.integrationId,
      replyMode: true,
    };
    // The reply composer pops OUT of the (often narrow) chat column into a
    // centered overlay — Gmail-compose style — so the To/Subject/Body fields
    // get real room instead of being squeezed to ~350px when a module is open
    // beside the chat. Portaled to <body> to escape the column's width clamp.
    const composer = (
      <EmailDraftCard
        data={replyData}
        onSend={async (edited) => {
          const r = await onCardAction({
            kind: 'email_draft',
            intent: 'send',
            data: { ...edited, threadId: selected.threadId, messageId: selected.messageId, accountId: selected.integrationId },
          });
          const ok = typeof r === 'boolean' ? r : undefined;
          if (ok) {
            // Delivered — the stash no longer holds unsaved work.
            replyDraftStash.delete(selected.messageId);
            setSelected(null);
          }
          return ok;
        }}
        onEdit={(fields) => replyDraftStash.set(selected.messageId, fields)}
        onCompose={async (args) => {
          const r = await onCardAction({
            kind: 'email_draft',
            intent: 'compose',
            data: {
              ...args,
              threadId: selected.threadId,
              messageId: selected.messageId,
              accountId: selected.integrationId,
              replyMode: true,
              subject: re,
            },
          });
          return r && typeof r === 'object' ? r : undefined;
        }}
      />
    );
    return createPortal(
      <div
        className="rc-inbox-reply-overlay xv-scroll"
        role="dialog"
        aria-modal="true"
        onClick={() => setSelected(null)}
        onKeyDown={(e) => {
          // Consume Escape HERE when focus is inside the overlay. The portal
          // still bubbles React events through ChatBox's tree, and ChatBox's
          // root onKeyDown (the chat-dock handler) runs BEFORE any window
          // listener — so without this, one Esc in the composer would dock the
          // chat pane AND act on this overlay. preventDefault marks the event
          // taken (the dock handler and useEscapeKey both decline prevented
          // events); stopPropagation keeps it from reaching ChatBox at all.
          if (e.key !== 'Escape' || e.defaultPrevented) return;
          e.preventDefault();
          e.stopPropagation();
          handleEscape();
        }}
      >
        <div ref={replyPanelRef} className="rc-inbox-reply-panel" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="rc-inbox-back" onClick={() => setSelected(null)}>
            ← Inbox
          </button>
          {composer}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="rc-card rc-inbox" data-testid="inbox-result-card">
      <div className="rc-meta">
        <span className="rc-meta-kind">📥 INBOX</span>
        <span className="rc-meta-sep">·</span>
        <span className="rc-meta-match">{emails.length}</span>
      </div>

      <div className="rc-inbox-grid">
        {emails.map((m) => (
          <button
            key={m.messageId}
            type="button"
            className="rc-inbox-chip"
            onClick={() => setSelected(m)}
            title={`${m.fromName || m.fromEmail} — ${decodeHtmlEntities(m.subject)}`}
          >
            {m.senderPhotoUrl ? (
              <img className="rc-av" src={m.senderPhotoUrl} alt="" />
            ) : (
              <div
                className="rc-av rc-av-initials"
                style={{ background: hueFor(m.fromEmail || m.fromName || m.messageId) }}
              >
                {initials(m.fromName, m.fromEmail || '')}
              </div>
            )}
            <div className="rc-inbox-text">
              <span className="rc-inbox-sender">
                {m.isUnread && <span className="rc-inbox-dot" aria-label="unread" />}
                {m.fromName || m.fromEmail || 'Unknown'}
              </span>
              <span className="rc-inbox-subject">{decodeHtmlEntities(m.subject) || '(no subject)'}</span>
              <span className="rc-inbox-snippet">{decodeHtmlEntities(m.snippet)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
